// The open loop API as this web app calls it. The pages were written against the legacy BFF; what they used from it
// keeps its name here (BffError, GENERIC_ERROR, query, seg), and the calls themselves go straight to the API:
// the access token lives in memory only (never localStorage), the refresh token is an HttpOnly cookie the API sets on
// its own /auth path, so script on this page can never read either of them (DESIGN § 3.3).

export const API_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:3100/api/v1').replace(/\/+$/, '');
/** Which web app this is, to the API (the refresh cookie and the e-mailed links are per app). */
export const APP: 'admin' | 'user' = 'admin';

/** The API's answer to a refused request: its code (what the page branches on) and its words. */
export class BffError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body: unknown = null,
    readonly code: string = `HTTP_${status}`,
  ) {
    super(message);
    this.name = 'BffError';
  }
}

export const GENERIC_ERROR = 'Something went wrong. Please try again.';

/** Kept for the pages that still call it: the API needs no CSRF token (Bearer, and the refresh call is guarded). */
export const setCsrfToken = (_token: string | null) => undefined;

let accessToken: string | null = null;
const signedOutListeners = new Set<() => void>();

export const setAccessToken = (token: string | null) => {
  accessToken = token;
};
export const hasAccessToken = () => accessToken !== null;
export const onSignedOut = (listener: () => void) => {
  signedOutListeners.add(listener);
  return () => signedOutListeners.delete(listener);
};

export interface ApiOptions {
  body?: unknown;
  /** Money requests: one key per logical submit, reused if the same submit is retried. */
  idempotencyKey?: string;
  /** Do not try a refresh on 401 (the refresh call itself, sign-in calls). */
  noRefresh?: boolean;
  /** Extra headers (a file's name with its bytes). */
  headers?: Record<string, string>;
}

async function send(method: string, path: string, options: ApiOptions): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
  const isFile = options.body instanceof Blob;
  if (options.body !== undefined) headers['Content-Type'] = isFile ? (options.body as Blob).type : 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
  // The cookies (refresh, remembered device) only travel to /auth; everything else is plain Bearer.
  const toAuth = path.startsWith('/auth/');
  if (toAuth) headers['X-Requested-With'] = 'ol';
  return fetch(`${API_URL}${path}`, {
    method,
    headers,
    body: options.body === undefined ? undefined : isFile ? (options.body as Blob) : JSON.stringify(options.body),
    credentials: toAuth ? 'include' : 'omit',
  });
}

interface Envelope<T> {
  data?: T;
  error?: { code: string; message: string; details?: unknown };
}

/** The API's words, with the reason for each refused field or password rule ("At least 10 characters."). */
function messageOf(error: NonNullable<Envelope<unknown>['error']>): string {
  const details = error.details;
  const reasons = Array.isArray(details)
    ? (details as { message?: string }[]).map((d) => d.message)
    : Array.isArray((details as { problems?: unknown } | undefined)?.problems)
      ? (details as { problems: string[] }).problems
      : [];
  const unique = [...new Set(reasons.filter((r): r is string => !!r))];
  return unique.length ? `${error.message} ${unique.map((r) => r.replace(/\.$/, '')).join('. ')}.` : error.message;
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  let json: Envelope<T> = {};
  try {
    json = text ? (JSON.parse(text) as Envelope<T>) : {};
  } catch {
    json = {};
  }
  if (!res.ok || json.error) {
    const error = json.error ?? { code: `HTTP_${res.status}`, message: GENERIC_ERROR };
    throw new BffError(res.status, messageOf(error), error, error.code);
  }
  return json.data as T;
}

let refreshing: Promise<boolean> | null = null;

/** One refresh at a time, shared by every request that found its token expired. */
export function refresh(): Promise<boolean> {
  refreshing ??= (async () => {
    for (let attempt = 0; attempt < 2; attempt++) {
      const res = await send('POST', '/auth/refresh', { body: { app: APP }, noRefresh: true }).catch(() => null);
      if (!res) return false;
      if (res.status === 409) {
        // Another tab refreshed a moment ago: its new cookie is already in the browser — try once more.
        await new Promise((r) => setTimeout(r, 300));
        continue;
      }
      if (!res.ok) return false;
      accessToken = (await parse<{ accessToken: string }>(res)).accessToken;
      return true;
    }
    return false;
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

export async function api<T = unknown>(method: string, path: string, options: ApiOptions = {}): Promise<T> {
  let res = await send(method, path, options);
  if (res.status === 401 && !options.noRefresh) {
    const body = (await res
      .clone()
      .json()
      .catch(() => null)) as { error?: { code?: string } } | null;
    const code = body?.error?.code;
    if (code === 'AUTH_TOKEN_EXPIRED' && (await refresh())) res = await send(method, path, options);
    else if (code === 'AUTH_SESSION_ENDED' || code === 'AUTH_REQUIRED' || code === 'AUTH_TOKEN_EXPIRED') {
      accessToken = null;
      signedOutListeners.forEach((l) => l());
    }
  }
  return parse<T>(res);
}

/** A file the API sends (a spreadsheet template), saved by the browser under `name`. */
export async function download(path: string, name: string) {
  let res = await send('GET', path, {});
  if (res.status === 401 && (await refresh())) res = await send('GET', path, {});
  if (!res.ok) throw new BffError(res.status, GENERIC_ERROR);
  const url = URL.createObjectURL(await res.blob());
  const link = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** A fresh key for one money submit. Keep it while retrying that same submit; make a new one for the next. */
export const newIdempotencyKey = () => crypto.randomUUID();

/** Path segment, as Uri.EscapeDataString. */
export const seg = (value: string) => encodeURIComponent(value);

/** A query string from the defined values (in order), with the leading "?" or nothing. */
export function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

// ───────────── the API's words as the legacy pages spelled them ─────────────

/** "issued_inactive" -> "IssuedInactive" (the legacy pages compare statuses in PascalCase). */
export const pascal = (value: string | null | undefined) =>
  value ? value.replace(/(^|_)([a-z])/g, (_, __: string, c: string) => c.toUpperCase()) : null;

const PROVIDER_NAMES: Record<string, string> = {
  intercash: 'InterCash',
  zumrails: 'ZumRails',
  mdp: 'Mdp',
  luxara: 'Luxara',
  opencp: 'Central Payments',
  gsc: 'Gsc',
};
/** "intercash" -> "InterCash", as the legacy pages name processors. */
export const providerName = (value: string | null | undefined) => (value ? (PROVIDER_NAMES[value] ?? value) : '');
/** And back. */
export const providerKey = (name: string | null | undefined) =>
  name ? (Object.entries(PROVIDER_NAMES).find(([, n]) => n === name)?.[0] ?? name.toLowerCase()) : null;

/** The money the API sends as "25.00" as the number the legacy pages format. */
export const amount = (value: string | null | undefined) =>
  value === null || value === undefined ? null : Number(value);
